import React, { useState, useEffect, useCallback } from 'react';
import { useLoading } from './LoadingOverlay/LoadingContext';
import { waitForNextPaint } from './LoadingOverlay/UtilsLoadingOverlay';

const AdminPage = () =>{  // 役割者管理ページ用コンポーネント
  // =========
  // 基本設定
  // =========
  const { showLoading, hideLoading } = useLoading();  // LoadingOverlayをセット
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);  // DBからデータリストを取得
  const [isFetchingList, setIsFetchingList] = useState(false);

  //表の項目
  const [newIDs, setNewIDs] = useState(['']);
  const [newEmails, setNewEmails] = useState(['']);
  const [newNames, setNewNames] = useState(['']);
  const [newNotes, setNewNotes] = useState(['']);
  const [newRoles, setNewRoles] = useState(['']);

  const [editBuffers, setEditBuffers] = useState({});
  const [sortOrder, setSortOrder] = useState('asc');  // 役割ソート用
  const [sortByRole, setSortByRole] = useState(false);  // 役割でソート中か
  const [showInputs, setShowInputs] = useState([false]);  // 入力欄表示用state

  const keyname = 'AdminPage_dataList';  // セッションストレージのキー名（ページ専用）
  //固定のAPIエンドポイントを指定
  const url = `https://kd4c0h9pca.execute-api.ap-northeast-1.amazonaws.com/Dev/LambdaToDynamoDB_DataEdit`;
  //固定URLにクエリパラメータを追加（Lambda関数でpage_nameを使って分岐させるため）
  const urlWithParams = new URL(url);
  urlWithParams.searchParams.set('page_name', 'AdminPage');
  
  // ========
  // API設定
  // ========
  // タイムアウト付きHTTPリクエスト関数
  const fetchWithTimeout = async (urlWithParams, options = {}, timeout = 5000) => {
    const controller = new AbortController();  // タイムアウト用コントローラーを作成
    const signal = controller.signal;  // fetchに渡すsignalを取得
    const fetchPromise = fetch(urlWithParams, { ...options, signal });  // fetchをsignal付きで渡す
    const timeoutId = setTimeout(() => {
      controller.abort();  // タイムアウトが来たらfetchを中断
    }, timeout);
    try {
      return await fetchPromise;  // fetchの結果を待つ
    } finally {
      clearTimeout(timeoutId);  // fetchが完了したらタイマーを解除
    }
  };

  // 再トライ付きAPI呼び出し関数（GET/POST両対応）
  const fetchAPI = async (urlWithParams, options = {}, retries = 2, timeout = 5000) => {
    let attempts = 0;
    let lastError = null;
    while (attempts < retries) {
      try {
        const response = await fetchWithTimeout(urlWithParams, options, timeout);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();  // 成功時にJSONデータを返す
      } catch (error) {
        lastError = error;
        attempts += 1;
        console.warn(`Attempt ${attempts} failed: ${error.message}`);
        if (attempts >= retries) {
            throw lastError;
        }
      }
    }
  };

  // 将来使用予定
  // ログインしていないユーザーをログインページへリダイレクトする
  // useEffect(() => {
  //   if (!auth) {
  //       navigate('/login');
  //   }
  // }, [auth, navigate]);

  // ===========
  // データ取得
  // ===========
  const GetList = useCallback(async ({ withLoading = true } ={}) => {
    setIsFetchingList(true);  // テーブルを隠す（非表示）
    if (withLoading) showLoading('データ読み込み中...');  // ユーザーに見せるメッセージ
    setError(null);
 
    // APIメソッドとデータ形式を特定
    const options = {
        method: 'GET',  // GETまたはPOST
        headers: {
            'Content-Type': 'application/json',  // 送受信するデータをJSON形式とするを示す
        },
    };

    try {
      const result = await fetchAPI(urlWithParams,options);
      console.log("GetList:::",result);
      
      // レスポンス形式を柔軟に処理
      let items = [];
      if (Array.isArray(result)) {
        items = result;
      } else if (result.Items && Array.isArray(result.Items)) {
        items = result.Items;
      } else if (result.body) {
        // Lambda関数がbodyプロパティでデータを返す場合
        const bodyData = typeof result.body === 'string' ? JSON.parse(result.body) : result.body;
        items = Array.isArray(bodyData) ? bodyData : (bodyData.Items || []);
      } else {
        console.warn('予期しないレスポンス形式:', result);
        items = [];
      }
      
      // データの形式を統一（ID, Name, Email, Role, Notes）
      const formattedData = items.map(item => ({
        ID: item.ID || item.id || '',
        Name: item.Name || item.name || '',
        Email: item.Email || item.email || '',
        Role: item.Role || item.role || '',
        Notes: item.Notes || item.notes || ''
      }));
      
      console.log('フォーマット済みデータ:', formattedData);
      setData(formattedData);  // 統一されたstateに保存
      setEditBuffers({});
    
      // データをブラウザのセッションストレージに保存
      // ページをリロードしても、同じセッション中ならデータを保持できる
      console.log('keyname:::',keyname);
      sessionStorage.setItem(keyname, JSON.stringify(formattedData));
    } catch (error) {
        console.error('API呼び出しエラー:', error);
        const errorMessage = error.message || 'データの取得に失敗しました。しばらくしてから再度お試しください。';
        setError(errorMessage);
        setData([]);  // エラー時は空の配列を設定
    } finally {
      setIsFetchingList(false);  // テーブルを戻す（表示）
      hideLoading();
    }
  }, [showLoading, hideLoading, keyname]);

  // ===========
  // データ登録
  // ===========
  const canRegisterNewRow = (index) => {
    const id = (newIDs[index] || '').trim();
    const name = (newNames[index] || '').trim();
    const email = (newEmails[index] || '').trim();
    const role = (newRoles[index] || '').trim();
    return !!showInputs[index] && !!id && !!name && !!email && !!role;
  };

  const cancelNewInput = (index) => {
    const newShowInputs = [...showInputs];
    newShowInputs[index] = false;
    setShowInputs(newShowInputs);

    const resetField = (setter) => {
      setter(prev => {
        const copy = [...prev];
        copy[index] = '';
        return copy;
      });
    };

    resetField(setNewIDs);
    resetField(setNewNames);
    resetField(setNewEmails);
    resetField(setNewRoles);
    resetField(setNewNotes);
  };

  // 登録ボタン押下時: 入力値をPOSTでDynamoDBへ登録
  const RegisterRole = async (index) => {
    if (!canRegisterNewRow(index)) {
      return;
    }

    const payload = {
      ID: (newIDs[index] || '').trim(),
      Name: (newNames[index] || '').trim(),
      Email: (newEmails[index] || '').trim(),
      Role: (newRoles[index] || '').trim(),
      Notes: (newNotes[index] || '').trim()
    };

    // 簡易バリデーション
    if (!payload.ID || !payload.Name || !payload.Email || !payload.Role) {
      setError('必須項目（社員番号・名前・Email・役割）を入力してください。');
      return;
    }

    showLoading('登録中...');
    setError(null);

    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    };

    let success = false;

    try {
      // fetchAPIは非200で例外を投げるため、409を捕捉してメッセージ表示
      await fetchAPI(urlWithParams, options);

      // 成功時: 初期化して一覧再取得
      const newShowInputs = [...showInputs];
      newShowInputs[index] = false;
      setShowInputs(newShowInputs);
      setNewIDs(['']);
      setNewNames(['']);
      setNewEmails(['']);
      setNewRoles(['']);
      setNewNotes(['']);
      await GetList({ withLoading: false });

      success = true;
    } catch (err) {
      const msg = err?.message ?? '';
      if (msg.includes('status: 409')) {
        setError('すでに登録済みです');
      } else {
        console.error('RegisterRole error:', err);
        setError('登録に失敗しました。時間をおいて再度お試しください。');
      }
    } finally {
      hideLoading();
    }

    if (success) {
      // Overlayの非表示反映を待ってから次の処理へ進む
      await waitForNextPaint();
      alert('登録しました。');
    }
  };

  // ===========
  // データ削除
  // ===========
  const DeleteRole = async (row) => {
    if (!row || !row.ID) {
      setError('削除対象の社員番号が不明です。');
      return;
    }

    const confirmed = window.confirm('本当に削除しますか');
    if (!confirmed) {
      return;
    }

    showLoading('削除中...');
    setError(null);

    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ID: row.ID })
    };

    let success = false;

    try {
      // page_name を保持したまま action=delete を付与した URL を生成
      const deleteUrl = new URL(urlWithParams);
      deleteUrl.searchParams.set('action', 'delete');
      await fetchAPI(deleteUrl, options);

      await GetList({ withLoading: false });

      success = true;
    } catch (err) {
      console.error('DeleteRole error:', err);
      setError('削除に失敗しました。時間をおいて再度お試しください。');
    } finally {
      hideLoading();
    }

    if (success) {
      // Overlayの非表示反映を待ってから次の処理へ進む
      await waitForNextPaint();
      alert('削除しました。');
    }
  };

  // ===========
  // データ更新
  // ===========
  const startEditRow = (row) => {
    if (!row || !row.ID || editBuffers[row.ID]) {
      return;
    }
    const normalized = {
      Name: row.Name || '',
      Email: row.Email || '',
      Role: row.Role || '',
      Notes: row.Notes || ''
    };
    setEditBuffers(prev => ({
      ...prev,
      [row.ID]: normalized
    }));
  };

  const handleEditFieldChange = (id, field, value) => {
    if (!id) return;
    setEditBuffers(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] || { Name: '', Email: '', Role: '', Notes: '' }),
        [field]: value
      }
    }));
  };

  const hasRowChanges = (row) => {
    if (!row || !row.ID || !editBuffers[row.ID]) {
      return false;
    }
    const buffer = editBuffers[row.ID];
    return ['Name', 'Email', 'Role', 'Notes'].some(key => {
      const currentValue = buffer[key] ?? '';
      const originalValue = (row[key] ?? '');
      return currentValue !== originalValue;
    });
  };

  const clearEditStateForRow = (id) => {
    setEditBuffers(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const UpdateRole = async (row) => {
    if (!row || !row.ID || !editBuffers[row.ID] || !hasRowChanges(row)) {
      return;
    }

    const buffer = editBuffers[row.ID];
    const payload = {
      ID: row.ID,
      Name: (buffer.Name || '').trim(),
      Email: (buffer.Email || '').trim(),
      Role: (buffer.Role || '').trim(),
      Notes: (buffer.Notes || '').trim()
    };

    if (!payload.Name || !payload.Email || !payload.Role) {
      setError('必須項目（名前・Email・役割）を入力してください。');
      return;
    }

    showLoading('更新中...');
    setError(null);

    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    };

    let success = false;

    try {
      // page_name を保持したまま action=update を付与した URL を生成
      const updateUrl = new URL(urlWithParams);
      updateUrl.searchParams.set('action', 'update');
      await fetchAPI(updateUrl, options);

      clearEditStateForRow(row.ID);
      await GetList({ withLoading: false });

      success = true;
    } catch (err) {
      console.error('UpdateRole error:', err);
      setError('更新に失敗しました。時間をおいて再度お試しください。');
    } finally {
      hideLoading();
    }

    if (success) {
      // Overlayの非表示反映を待ってから次の処理へ進む
      await waitForNextPaint();
      alert('更新しました。');
    }
  };

  // ====================
  // 初回マウント時の処置
  // ====================
  // 初回マウント時にセッションストレージからデータを取得し、画面表示。 データがなければAPIを呼び出す
  useEffect(() => {
    const datalist = sessionStorage.getItem(keyname);
    // Datalistが存在し、内容に"null"という文字がないこと
    if (datalist && datalist != 'null') {  // セッションストレージ内にNullが生成されるため。その状態でlistに遷移するとmapできずエラーとなるため回避
      // Reactステートに取得データをセット（統一されたstateに保存）
      setData(JSON.parse(datalist));
        console.log("リスト取得完了");
    } else {
        // 有効なデータがない場合はAPIから取得
        GetList();
    }
  }, []);

  console.log("data:::",data);

  // =========
  // 表の機能
  // =========
  // 【管理者登録表】
  // 社員番号選択処理
  const handleIDChange = (index, value) => {
    const newIDsCopy = [...newIDs];
    newIDsCopy[index] = value;
    setNewIDs(newIDsCopy);
  };
  // 名前入力処理
  const handleNameChange = (index, value) => {
    const newNamesCopy = [...newNames];
    newNamesCopy[index] = value;
    setNewNames(newNamesCopy);
  };
  // メールアドレス入力処理
  const handleEmailChange = (index, value) => {
    const newEmailsCopy = [...newEmails];
    newEmailsCopy[index] = value;
    setNewEmails(newEmailsCopy);
  };
  // 役割入力処理
  const handleRoleChange = (index, value) => {
    const newRolesCopy = [...newRoles];
    newRolesCopy[index] = value;
    setNewRoles(newRolesCopy);
  };
  // 備考入力処理
  const handleNotesChange = (index, value) => {
    const newNotesCopy = [...newNotes];
    newNotesCopy[index] = value;
    setNewNotes(newNotesCopy);
  };

  // ドロップダウン用の役割者オプション
  const roleOptions = ['担当者', '承認者', 'SE'];

  // 【登録済み役割者表】
  // ソート処理
  const sortedData = sortByRole
    ? [...data].sort((a, b) => {
        if (a.Role < b.Role) return sortOrder === 'asc' ? -1 : 1;
        if (a.Role > b.Role) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      })
    : data;

  // 役割ヘッダークリック時
  const handleRoleSort = () => {
    if (!sortByRole) {
      setSortByRole(true);
      setSortOrder('asc');
    } else {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    }
  };

  return (
    <div className='wrapper'>
      <h2 className="page-title">役割者管理</h2>
      
      {/* エラー表示 */}
      {error && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          fontSize: '0.6em', 
          color: '#ff4444',
          backgroundColor: '#ffe6e6',
          border: '1px solid #ff4444',
          borderRadius: '4px',
          margin: '10px 0'
        }}>
          {error}
          <button 
            onClick={GetList}
            style={{
              marginLeft: '10px',
              padding: '4px 8px',
              fontSize: '0.5em',
              backgroundColor: '#ff4444',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            再試行
          </button>
        </div>
      )}
      
      {/* ローディング中やエラー時はテーブルを非表示 */}
      {!isFetchingList && !error && (
        <div className='content-area'>
          <h3 className='table-title' style={{ margin: '10px 0 0 0.3em' }}>役割者登録　　　※入力必須項目</h3>
          <div className='table-area table-area-spaced'>
            <table>
              <thead>
                <tr>
                  <th style={{ minWidth: '90px' }}>社員番号 ※</th>
                  <th>名前 ※</th>
                  <th>Email ※</th>
                  <th>役割 ※</th>
                  <th>備考</th>
                  <th>編集</th>
                </tr>
              </thead>
              <tbody>
                {[0].map((idx) => (
                  <tr key={idx}>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newIDs[idx] || ''}
                          onChange={(e) => handleIDChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newNames[idx] || ''}
                          onChange={(e) => handleNameChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newEmails[idx] || ''}
                          onChange={(e) => handleEmailChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <select
                          value={newRoles[idx] || ''}
                          onChange={(e) => handleRoleChange(idx, e.target.value)}
                          style={{
                            fontSize: '1.0em',
                            width: '100%',
                            border: 'none',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        >
                          <option value="">選択してください</option>
                          {roleOptions.map(role => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      ) : null}
                    </td>
                    <td>
                      {showInputs[idx] ? (
                        <input
                          type="text"
                          value={newNotes[idx] || ''}
                          onChange={(e) => handleNotesChange(idx, e.target.value)}
                          style={{
                            width: '100%',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '1.0em',
                            backgroundColor: '#f5f5f5',
                            color: '#333333'
                          }}
                        />
                      ) : null}
                    </td>
                    <td>
                      <div className='button-group'>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          <button 
                            style={{ 
                              backgroundColor: '#44aa44', 
                              opacity: showInputs[idx] ? 0.6 : 1,
                              cursor: showInputs[idx] ? 'not-allowed' : 'pointer'
                            }}
                            disabled={showInputs[idx]}
                            onClick={() => {
                              const newShowInputs = [...showInputs];
                              newShowInputs[idx] = true;
                              setShowInputs(newShowInputs);
                            }}
                          >
                            {showInputs[idx] ? '入力中' : '入力'}
                          </button>
                          {showInputs[idx] && (
                            <button 
                              style={{ backgroundColor: '#888888', }}
                              onClick={() => cancelNewInput(idx)}
                            >
                              キャンセル
                            </button>
                          )}
                          <button 
                            style={{ 
                              backgroundColor: canRegisterNewRow(idx) ? '#4444ff' : '#9aa0ff', 
                              cursor: canRegisterNewRow(idx) ? 'pointer' : 'not-allowed',
                              opacity: canRegisterNewRow(idx) ? 1 : 0.6
                            }}
                            disabled={!canRegisterNewRow(idx)}
                            onClick={() => RegisterRole(idx)}
                          >
                            登録
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      
          <h3 className='table-title'>登録済み役割者　　　※入力必須項目</h3>
          <div className='table-area'>
            <table>
              <thead>
                <tr>
                  <th style={{ minWidth: '90px' }}>社員番号</th>
                  <th>名前 ※</th>
                  <th>Email ※</th>
                  <th
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={handleRoleSort}
                  >
                    役割 ※
                    <span style={{ color: sortOrder === 'asc' && sortByRole ? '#222' : '#888', marginLeft: '2px' }}>▲</span>
                    <span style={{ color: sortOrder === 'desc' && sortByRole ? '#222' : '#888' }}>▼</span>
                  </th>
                  <th>備考</th>
                  <th>編集</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, idx) => {
                  const isEditing = !!editBuffers[row.ID];
                  const buffer = editBuffers[row.ID] || {};
                  const canUpdate = isEditing && hasRowChanges(row);
                  return (
                    <tr key={idx}>
                      <td>{row.ID}</td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={buffer.Name ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ID, 'Name', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.Name}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={buffer.Email ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ID, 'Email', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.Email}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            value={buffer.Role ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ID, 'Role', e.target.value)}
                            style={{
                              fontSize: '1.0em',
                              width: '100%',
                              border: 'none',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          >
                            <option value="">選択してください</option>
                            {roleOptions.map(role => (
                              <option key={role} value={role}>{role}</option>
                            ))}
                          </select>
                        ) : row.Role}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={buffer.Notes ?? ''}
                            onChange={(e) => handleEditFieldChange(row.ID, 'Notes', e.target.value)}
                            style={{
                              width: '100%',
                              border: 'none',
                              textAlign: 'left',
                              fontSize: '1.0em',
                              backgroundColor: '#f5f5f5',
                              color: '#333333'
                            }}
                          />
                        ) : row.Notes}
                      </td>
                      <td>
                        <div className='button-group'>
                          <button 
                            style={{ 
                              backgroundColor: '#44aa44', 
                              opacity: isEditing ? 0.6 : 1,
                              cursor: isEditing ? 'not-allowed' : 'pointer'
                            }}
                            disabled={isEditing}
                            onClick={() => startEditRow(row)}
                          >
                            {isEditing ? '編集中' : '編集'}
                          </button>
                          {isEditing && (
                            <button 
                              style={{ backgroundColor: '#888888' }}
                              onClick={() => clearEditStateForRow(row.ID)}
                            >
                              キャンセル
                            </button>
                          )}
                          <button 
                            style={{ 
                              backgroundColor: canUpdate ? '#4444ff' : '#9aa0ff', 
                              cursor: canUpdate ? 'pointer' : 'not-allowed',
                              opacity: canUpdate ? 1 : 0.6
                            }}
                            disabled={!canUpdate}
                            onClick={() => UpdateRole(row)}
                          >
                            登録
                          </button>
                          <button 
                            style={{ backgroundColor: '#ff4444' }}
                            onClick={() => DeleteRole(row)}
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
             </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
